import { produce as createNextState, isDraft, isDraftable } from 'immer';
function freezeDraftable(val) {
    return isDraftable(val) ? createNextState(val, () => { }) : val;
}
function isStateFunction(x) {
    return typeof x === 'function';
}
/** @inheritdoc */
export function createReducer(initialState, mapOrBuilderCallback) {
    const [actionsMap, finalActionMatchers, finalDefaultCaseReducer] = executeReducerBuilderCallback(mapOrBuilderCallback);
    // Ensure the initial state gets frozen either way (if draftable)
    let getInitialState;
    // A lazy initializer must be invoked before its result can be frozen
    if (isStateFunction(initialState)) {
        getInitialState = () => freezeDraftable(initialState());
    }
    else {
        const frozenInitialState = freezeDraftable(initialState);
        getInitialState = () => frozenInitialState;
    }
    function reducer(state, action) {
        let caseReducers = [
            actionsMap[action.type],
            ...finalActionMatchers
                // Keep only matchers whose predicate matches this action
                .filter(({ matcher }) => matcher(action))
                // Extract just the matched reducers
                .map(({ reducer }) => reducer),
        ];
        // Fall back to the default case reducer when nothing else matched this action
        // Check whether any slot actually holds a reducer
        const hasMatchedReducer = caseReducers.filter((cr) => !!cr).length > 0;
        // Only substitute the default case when no case/matcher reducer actually matched
        if (finalDefaultCaseReducer && !hasMatchedReducer) {
            caseReducers = [finalDefaultCaseReducer];
        }
        // Run each matched case reducer against the previous state, producing the next state
        return caseReducers.reduce((previousState, caseReducer) => {
            // Only transform state when a case reducer was matched for this slot
            if (caseReducer) {
                // Reuse an existing draft when already inside a `createNextState` call
                if (isDraft(previousState)) {
                    // If it's already a draft, we must already be inside a `createNextState` call,
                    // likely because this is being wrapped in `createReducer`, `createSlice`, or nested
                    // inside an existing draft. It's safe to just pass the draft to the mutator.
                    const draft = previousState; // We can assume this is already a draft
                    const result = caseReducer(draft, action);
                    // Treat an undefined result as "no change" rather than clearing the state
                    if (result === undefined) {
                        return previousState;
                    }
                    return result;
                }
                else if (!isDraftable(previousState)) {
                    // If state is not draftable (ex: a primitive, such as 0), we want to directly
                    // return the caseReducer func and not wrap it with produce.
                    const result = caseReducer(previousState, action);
                    // Treat an undefined result as "no change", unless there's no previous state to fall back to
                    if (result === undefined) {
                        // A `null` previous state has nothing to fall back to, so undefined is legitimately "no change"
                        if (previousState === null) {
                            return previousState;
                        }
                        throw Error('A case reducer on a non-draftable value must not return undefined');
                    }
                    return result;
                }
                else {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    // createNextState() produces an Immutable<Draft<S>> rather
                    // than an Immutable<S>, and TypeScript cannot find out how to reconcile
                    // these two types.
                    return createNextState(previousState, (draft) => {
                        return caseReducer(draft, action);
                    });
                }
            }
            return previousState;
        }, state ?? getInitialState());
    }
    reducer.getInitialState = getInitialState;
    return reducer;
}
/**
 * Executes a reducer builder callback against a fresh {@link ActionReducerMapBuilder},
 * collecting the case reducers, matchers, and default case it registered.
 *
 * @template TState - The reducer's state type.
 * @template TAction - The union of actions the reducer handles.
 * @param builderCallback - The callback passed to {@link createReducer} that registers cases via the builder.
 * @returns A tuple of the action-to-reducer map, matcher list, and optional default case reducer.
 */
function executeReducerBuilderCallback(builderCallback) {
    const actionsMap = {};
    const actionMatchers = [];
    let defaultCaseReducer;
    const builder = {
        addCase(typeOrActionCreator, reducer) {
            // Skip these dev-only ordering checks in production builds to avoid the extra overhead
            if (process.env.NODE_ENV !== 'production') {
                /*
                 * to keep the definition by the user in line with actual behavior,
                 * we enforce `addCase` to always be called before calling `addMatcher`
                 * as matching cases take precedence over matchers
                 */
                if (actionMatchers.length > 0) {
                    throw new Error('`builder.addCase` should only be called before calling `builder.addMatcher`');
                }
                // A default case must always be registered last, after every specific case
                if (defaultCaseReducer) {
                    throw new Error('`builder.addCase` should only be called before calling `builder.addDefaultCase`');
                }
            }
            const type = typeof typeOrActionCreator === 'string' ? typeOrActionCreator : typeOrActionCreator.type;
            // Each action type may only be handled by a single case reducer
            if (type in actionsMap) {
                throw new Error('addCase cannot be called with two reducers for the same action type');
            }
            actionsMap[type] = reducer;
            return builder;
        },
        addMatcher(matcher, reducer) {
            // Skip this dev-only ordering check in production builds to avoid the extra overhead
            if (process.env.NODE_ENV !== 'production') {
                // A default case must always be registered last, after every matcher
                if (defaultCaseReducer) {
                    throw new Error('`builder.addMatcher` should only be called before calling `builder.addDefaultCase`');
                }
            }
            actionMatchers.push({ matcher, reducer });
            return builder;
        },
        addDefaultCase(reducer) {
            // Skip this dev-only uniqueness check in production builds to avoid the extra overhead
            if (process.env.NODE_ENV !== 'production') {
                // Only one default case reducer may be registered per builder
                if (defaultCaseReducer) {
                    throw new Error('`builder.addDefaultCase` can only be called once');
                }
            }
            defaultCaseReducer = reducer;
            return builder;
        },
    };
    builderCallback(builder);
    return [
        actionsMap,
        actionMatchers,
        defaultCaseReducer,
    ];
}
//# sourceMappingURL=create-reducer.js.map